import { Alert, Box, Button, ButtonGroup, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, InputLabel, ListItemText, Menu, MenuItem, Select, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { IFacturaMetodoPago } from "./FacturarCitaDialog";
import { useEffect, useState } from "react";
import React from "react";
import ConfigUtils from "../../../utils/Config";
import FacturacionService from "../../../services/FacturacionService";
import { useNavigate } from "react-router-dom";
import LogService from "../../../services/LogService";
import ILog from "../../../types/Log";
import * as constantes from "../../../common/Constantes";
import IAlert from "../../../types/Alert";
import DownloadIcon from '@mui/icons-material/GetApp';
import EmailIcon from '@mui/icons-material/Email';
import MoreIcon from '@mui/icons-material/MoreVert';
import DocUtils from "../../../utils/Doc";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { IConcepto, IImpuesto } from "./DetalleFactura";
import { IFactura } from "../Facturas";
import IPaciente from "../../../types/Paciente";
import { getEstadoFacturacion } from "../common/EstadoFacturacionIcon";
import ComplementariaFormDialog from "./ComplementariaFormDialog";
import PacientesUtils from "../../../utils/Pacientes";
import { formatCurrency, formatNumeroFactura } from "../../../utils/Factura";
import Utilities from "../../../common/Utilities";
import { getFacturaAcciones } from "../common/facturaAcciones";
import NumeroOrientativoEmision, { cargarNumeroOrientativo, InfoNumeroOrientativo } from "../common/NumeroOrientativoEmision";
import { chipEstadoFactura, chipEstadoPago } from "../common/chipsEstadoFactura";
import EnlaceFactura from "../common/EnlaceFactura";

export interface IFacturaCompleta {
  _id: string;
  numero?: string;
  serie: string;
  tipo: "cita" | "clase" | "bono" | "agrupada";
  fechaFactura: Date;
  fechaEmision?: Date;
  moneda: string;
  estado: "Borrador" | "Emitida" | "Anulada";
  estadoPago: "Pendiente" | "Pagada";
  metodoPago?: IFacturaMetodoPago;
  bonoId?: string; // obligatorio si metodoPago es bono
  servicio: string;
  conceptos: IConcepto[];
  impuestos: IImpuesto[];
  importeBase: number;
  importeTotal: number;
  receptor: {
    nombre: string;
    nif?: string;
    direccion?: string;
    codigoPostal?: string;
    provincia?: string;
    pais?: string;
    email?: string;
  };
  emisor: {
    razonSocial: string;
    nif?: string;
    direccion?: string;
    codigoPostal?: string;
    ciudad?: string;
    pais?: string;
    email?: string;
  };
  facturaOriginalId?: string;
  facturaOriginalRef?: string | null;
  activo: boolean;
  userId: string;
  facturaRectificada?: string;
  facturaRectificadaId?: string | null;
  tipoRectificacion?: string | null;
  facturaOriginalRelacionadaId?: string;
  motivoAnulacion?: string;
  pacienteId?: string;
  entityId?: string | null;
}

interface DetalleFacturaComplementariaProps {
  open: boolean;
  onClose: () => void;
  factura: IFactura;
  facturasParciales: IFactura[];
  pacientes: IPaciente[];
  onFacturaPagada: (factura: IFactura) => void;
  onFacturaEmitida: (factura: IFactura) => void;
  onFacturaActualizada: ({ facturaComplementaria, parcialesAsociadas, parcialesDesasociadas }: { facturaComplementaria: IFacturaCompleta, parcialesAsociadas: string[], parcialesDesasociadas: string[] }) => void;
  onFacturaRectificada?: ({ factura }: { factura: IFactura }) => void;
}

const DetalleFacturaComplementaria = ({ open, onClose, factura, facturasParciales, pacientes, onFacturaPagada, onFacturaEmitida, onFacturaActualizada, onFacturaRectificada }: DetalleFacturaComplementariaProps ) => {
  const history = useNavigate();
  const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
  const [loadingInit, setLoadingInit] = useState(false);
  const [facturaComplementaria, setFacturaComplementaria] = useState<IFacturaCompleta | null>(null);
  const monedaFactura = facturaComplementaria?.moneda || 'EUR';
  const acciones = getFacturaAcciones(facturaComplementaria ?? {});
  const paciente = facturaComplementaria
    ? Utilities.resolverPacienteFactura(pacientes, facturaComplementaria)
    : null;

  const [expandirDetalle, setExpandirDetalle] = useState(false);
  const [modalPagar, setModalPagar] = useState(false);
  const [metodoPago, setMetodoPago] = useState<IFacturaMetodoPago | undefined>(undefined);
  const [errorMetodoPago, setErrorMetodoPago] = useState<string | undefined>(undefined);
  const [errorFechaPago, setErrorFechaPago] = useState<string | undefined>(undefined);
  const [modalEmitir, setModalEmitir] = useState(false);
  const [numeroOrientativo, setNumeroOrientativo] = useState<InfoNumeroOrientativo | null>(null);
  const [erroresPaciente, setErroresPaciente] = useState<string[]>([]);
  const [loadingPagar, setLoadingPagar] = useState(false);
  const [loadingEmitir, setLoadingEmitir] = useState(false);
  const [loadingEnviar, setLoadingEnviar] = useState(false);
  const [showMissingAlert, setShowMissingAlert] = useState(true);
  const [modalEditarComplementaria, setModalEditarComplementaria] = useState(false);
  const [facturasParcialesIds, setFacturasParcialesIds] = useState<string[]>(facturasParciales.map(f => f.numero).filter((n): n is string => !!n));
  const [modalRectificar, setModalRectificar] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const metodosPago = ConfigUtils.getMetodosPago();
  const userRole: number = ConfigUtils.getUserRole();

  const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

  useEffect(() => {
    setLoadingInit(true);
  
    const getFacturaById = async () => {
      try {
        const response = await FacturacionService.getFacturaById(factura._id);
        setFacturaComplementaria(response.data || null);
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "DetalleFacturaComplementaria.Component.getFacturaById",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingInit(false);
      }
    };
  
    getFacturaById();
  }, [factura]);

  const openModalPagar = () => {
    if (facturaComplementaria?.estadoPago === "Pagada" && facturaComplementaria.metodoPago) {
      setMetodoPago({
        nombre: facturaComplementaria.metodoPago.nombre,
        codigo: facturaComplementaria.metodoPago.codigo,
        fechaPago: moment(facturaComplementaria.metodoPago.fechaPago),
      });
    }
    setModalPagar(true);
  };

  const openModalEmitir = async () => {
    const errores: string[] = [];

    if (!facturaComplementaria?.receptor?.nombre) errores.push("Nombre");
    if (!facturaComplementaria?.receptor?.nif) errores.push("DNI");

    if (errores.length > 0) {
      setErroresPaciente(errores);
    }
    setNumeroOrientativo(errores.length === 0 ? await cargarNumeroOrientativo(facturaComplementaria?._id) : null);
    setModalEmitir(true);
  };

  const openModalRectificar = () => {
    setModalRectificar(true);
  };

  const closeModalPagar = () => {
    setMetodoPago(undefined);
    setErrorMetodoPago(undefined);
    setErrorFechaPago(undefined);
    setModalPagar(false);
  };

  const closeModalEmitir = () => {
    setErroresPaciente([]);
    setModalEmitir(false);
  };

  const pagarFactura = async () => {
    let hasError = false;

    if (!metodoPago) {
      setErrorMetodoPago("Debes seleccionar un método de pago");
      hasError = true;
    }
    if (!metodoPago?.fechaPago) {
      setErrorFechaPago("Debes seleccionar una fecha de pago");
      hasError = true;
    } else if (!moment.isMoment(metodoPago.fechaPago) || !metodoPago.fechaPago.isValid()) {
      setErrorFechaPago("Debes seleccionar una fecha de pago válida");
      hasError = true;
    }

    if (hasError) return;
    if (!metodoPago) return;

    setErrorMetodoPago(undefined);
    setErrorFechaPago(undefined);

    try {
      setLoadingPagar(true);
      const response = await FacturacionService.pagarFactura(factura._id, { 
        tipoPago: "complementaria",
        metodoPago });

      const facturaActualizada: IFactura = {
        _id: response.data.factura._id,
        numero: response.data.factura.numero,
        fechaFactura: response.data.factura.fechaFactura,
        tipo: response.data.factura.tipo,
        modalidad: response.data.tipoFacturacion,
        servicio: response.data.factura.servicio,
        pacienteId: "",
        paciente: response.data.factura.receptor?.nombre ?? null,
        servicioId: "",
        total: response.data.factura.importeTotal,
        estado: getEstadoFacturacion(response.data.factura.estado, response.data.factura.estadoPago),
        facturaRelacionada: undefined,
        fechaPago: response.data.factura.metodoPago?.fechaPago?.toString() ?? null
      };

      onFacturaPagada(facturaActualizada);
      closeModalPagar();

    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "DetalleFacturaComplementaria.Component.pagarFactura",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoadingPagar(false);
    }
  };

  const emitirFactura = async () => {
    try {
      setLoadingEmitir(true);
      const response = await FacturacionService.emitirFactura(factura._id, { 
        userId: ConfigUtils.getUserId(),
        tipoPago: "complementaria" });
      
      const facturaActualizada: IFactura = {
        _id: response.data.factura._id,
        numero: response.data.factura.numero,
        fechaFactura: response.data.factura.fechaFactura,
        tipo: response.data.factura.tipo,
        modalidad: response.data.tipoFacturacion,         
        servicio: response.data.factura.servicio,
        pacienteId: "",
        paciente: response.data.factura.receptor?.nombre ?? null,
        servicioId: "",
        total: response.data.factura.importeTotal,
        estado: getEstadoFacturacion(response.data.factura.estado, response.data.factura.estadoPago),
        facturaRelacionada: undefined
      };

      onFacturaEmitida(facturaActualizada);
      closeModalEmitir();

    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "DetalleFacturaComplementaria.Component.emitirFactura",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoadingEmitir(false);
    }
  };

  const handleDownloadFactura = () => {
    DocUtils.formatFactura(facturaComplementaria, true, "complementaria", undefined, facturasParciales.map(f => f.numero).join(", "));
  };

  const handleEnviarFactura = async () => {
    try {
      setLoadingEnviar(true);
      const facturaPDF = DocUtils.formatFactura(facturaComplementaria, false, "complementaria", undefined, facturaComplementaria?.numero);
      const response = await FacturacionService.sendFactura({ facturaId: facturaComplementaria?._id, email: paciente?.email, doc: facturaPDF, nombre: facturaComplementaria?.receptor.nombre, numeroFactura: facturaComplementaria?.numero });
      setAlert({ open: true, severity: 'success', msg: response.data.message });
    } catch (e: any) {
      if (e.response.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
        } else {
            const error: ILog = { method: 'DetalleFacturaComplementaria.Component.sendFactura', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
        }
    } finally {
      setLoadingEnviar(false);
    }
  };

  const missingFields = paciente
  ? [
      !paciente.domicilio ? "Dirección" : null,
      !paciente.DNI ? "NIF" : null,
    ].filter((f): f is string => Boolean(f))
  : [];

  const { isDisabledEmail, tooltipMsg } = PacientesUtils.canSendFactura(
    loadingEnviar,
    paciente?.authEmail,
    paciente?.email,
    facturaComplementaria?.estado,
    facturaComplementaria?.facturaRectificadaId
  );

  return (
      <>
        <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
          {loadingInit ? <CircularProgress style={{ margin: "20px auto" }} /> : <>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                border: "1px solid #e5e5e5",
              }}
            >
              {/* Aviso de datos incompletos */}
              {missingFields.length > 0 && showMissingAlert && facturaComplementaria && facturaComplementaria.estado !== "Emitida" && (
              <Alert
                severity="warning"
                sx={{ mb: 1, fontSize: "0.875rem" }}
                onClose={() => setShowMissingAlert(false)}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">
                    Faltan datos importantes: <b>{missingFields.join(", ")}</b>.
                  </Typography>
      
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => history(constantes.R_NUEVOPACIENTE, { state: paciente })}
                  >
                    Editar
                  </Button>
                </Box>
              </Alert>
              )}
              
              <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between">
                {/* Datos del receptor */}
                <Box flex={1}>
                  <Typography variant="h6" gutterBottom>
                    Receptor
                  </Typography>
                  <Typography variant="body2">
                    <b>Nombre:</b> {facturaComplementaria?.receptor.nombre}
                  </Typography>
                  {facturaComplementaria?.receptor.nif && 
                  <Typography variant="body2">
                    <b>NIF:</b> {facturaComplementaria?.receptor.nif}
                  </Typography>}
                  {facturaComplementaria?.receptor.direccion && 
                  <Typography variant="body2">
                    <b>Domicilio:</b> {facturaComplementaria?.receptor.direccion}
                  </Typography>}
                  {facturaComplementaria?.receptor.email &&
                  <Typography variant="body2">
                    <b>Email:</b> {facturaComplementaria?.receptor.email}
                  </Typography>}
                </Box>

                {/* Emisor — solo si hay más de una entidad de facturación configurada */}
                {facturaComplementaria?.emisor && ConfigUtils.getDatosFacturacionAdicional().length > 0 && (
                  <Box flexShrink={0} mt={{ xs: 2, sm: 0 }}>
                    <Typography variant="h6" gutterBottom>
                      Emisor
                    </Typography>
                    <Typography variant="body2">
                      <b>Nombre:</b> {facturaComplementaria.emisor.razonSocial}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <DialogContent>
              {/* INFO GENERAL DE LA FACTURA */}
              <Box mb={isMobile ? 2 : 3}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600 }}
                  >
                    Información de la factura
                  </Typography>       
                  {/* Acciones */}
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="Descargar factura">
                      <IconButton
                        size="small"
                        onClick={handleDownloadFactura}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={factura.estado === "anulada" ? "Factura anulada" : tooltipMsg}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleEnviarFactura}
                          disabled={isDisabledEmail || factura.estado === "anulada"}
                        >
                          {loadingEnviar ? (
                            <CircularProgress size={18} />
                          ) : (
                            <EmailIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>        
                    
                <Box mb={1}>
                  {/* --- Fila principal: Nº, Fecha, Estado --- */}
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    justifyContent="flex-start"
                    alignItems={isMobile ? "flex-start" : "center"}
                    spacing={isMobile ? 1 : 2}
                  >
                    {/* Nº */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Nº
                      </Typography>
                      <Typography variant="body2">{formatNumeroFactura(factura.numero)}</Typography>
                    </Stack>

                    {/* Fecha */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Fecha
                      </Typography>
                      <Typography variant="body2">{new Date(factura.fechaFactura!).toLocaleDateString()}</Typography>
                    </Stack>

                    {/* Estado */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 140 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Estado
                      </Typography>
                      {factura.estado === "anulada" ? (
                        <Tooltip
                          title={factura?.motivoAnulacion}
                          placement="top"
                          arrow
                        >
                          <Chip
                            label={factura.estado}
                            size="small"
                            sx={{ textTransform: "capitalize", border: `1px solid #d32f2f`, color: "#d32f2f" }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          {...chipEstadoFactura((facturaComplementaria ?? factura))}
                          size="small"
                        />
                      )}
                    </Stack>
                  </Stack>

                  {/* --- Fila secundaria: Facturas parciales --- */}
                  {facturasParcialesIds.length > 0 && (
                    <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        Facturas parciales Nºs:{" "}
                      </Box>
                      <Box component="span" sx={{ fontWeight: "normal" }}>
                        {facturasParcialesIds.map((n, i) => (
                          <React.Fragment key={n}>
                            {i > 0 && ", "}
                            <EnlaceFactura facturaId={facturasParciales.find(f => f.numero === n)?._id} numero={n} />
                          </React.Fragment>
                        ))}
                      </Box>
                    </Typography>
                  )}

                  {/* --- Fila final: Información de rectificación --- */}
                  {(facturaComplementaria?.facturaOriginalId || facturaComplementaria?.facturaRectificadaId) && (
                    <Stack direction="column" spacing={0.5} sx={{ mt: 0.5 }}>
                      {facturaComplementaria?.facturaOriginalId && (
                        <Stack direction="row" spacing={1}>
                          <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                            {facturaComplementaria?.tipoRectificacion === "anulacion" ? "Anulación de la factura nº" : "Rectificativa de la factura nº"}
                          </Typography>
                          <EnlaceFactura facturaId={facturaComplementaria.facturaOriginalRef} numero={facturaComplementaria.facturaOriginalId} cursiva />
                        </Stack>
                      )}
                      {facturaComplementaria?.facturaRectificadaId && (
                        <Stack direction="row" spacing={1}>
                          <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                            {facturaComplementaria?.estado === "Anulada" ? "Anulada por la factura nº" : "Rectificada por la factura nº"}
                          </Typography>
                          <EnlaceFactura facturaId={facturaComplementaria.facturaRectificadaId} numero={facturaComplementaria.facturaRectificada} cursiva />
                        </Stack>
                      )}
                    </Stack>
                  )}
                </Box>

              </Box>
              <Divider sx={{ mb: 2 }} />                   
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="space-between"
                alignItems={isMobile ? "stretch" : "center"}
                mb={2}
                gap={isMobile ? 1.5 : 4}
              >
                {/* INFO IZQUIERDA */}
                <Box display="flex" flexDirection="column" gap={isMobile ? 0.75 : 1}>
                  {/* Estado de pago */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={{ xs: 0, sm: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Estado
                    </Typography>
                    <Chip
                      {...chipEstadoPago(facturaComplementaria)}
                      size="small"
                    />
                  </Box>
      
                  {/* Tipo de facturación */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={{ xs: 0, sm: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Tipo
                    </Typography>
                    <Chip
                      label={"Complementaria"}
                      size="small"
                      color="info"
                    />
                  </Box>
      
                  {/* Servicio */}
                  <Box display="flex" justifyContent="space-between" gap={{ xs: 0, sm: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Servicio
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {facturaComplementaria?.servicio}
                    </Typography>
                  </Box>
                </Box>
      
                {/* IMPORTE */}
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems={isMobile ? "flex-start" : "flex-end"}
                  mt={isMobile ? 1 : 0}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Importe
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {formatCurrency(Number(facturaComplementaria?.importeTotal), monedaFactura)}
                  </Typography>
                </Box>
              </Box>
              {/* Si la factura está pagada, método de pago */}
              {facturaComplementaria?.estadoPago === "Pagada" && (
              <Box
                mb={3}
                p={2}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  backgroundColor: "background.paper",
                  maxWidth: 400,
                }}
              >
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="body2">
                    <b>Método de pago:</b> {facturaComplementaria?.metodoPago?.nombre}
                  </Typography>
                  <Typography variant="body2">
                    <b>Fecha de pago:</b> {moment(facturaComplementaria?.metodoPago?.fechaPago).format('DD/MM/YYYY')}
                  </Typography>
                </Box>
              </Box>
              )}              
              <Divider />
              <Box mt={2} display="flex" justifyContent="space-between">
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={() => setExpandirDetalle(!expandirDetalle)}
                >
                  {expandirDetalle ? "Ocultar detalles" : "Ver detalles"}
                </Button>

                <Box display="flex" gap={1} alignItems="center">

                {acciones.mostrarBotonesAccion && (
                  <ButtonGroup variant="outlined" color="success">
                    {acciones.mostrarPagar && (
                      <Button variant={acciones.variantPagar} onClick={openModalPagar}>Pagar</Button>
                    )}
                    {acciones.mostrarEmitir && (
                      <Button variant={acciones.variantEmitir} onClick={openModalEmitir}>Emitir</Button>
                    )}
                  </ButtonGroup>
                )}
                {userRole === constantes.ROLES.ADMINISTRADOR &&
                  acciones.mostrarMenuAcciones && (
                    <>
                      <Tooltip title="Más acciones">
                        <IconButton
                          size="small"
                          onClick={(e) => setAnchorEl(e.currentTarget)}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                      >
                        {acciones.mostrarCorregirCobro && <MenuItem
                          onClick={() => {
                            setAnchorEl(null);
                            openModalPagar();
                          }}
                        >
                          <ListItemText>
                            Corregir cobro
                          </ListItemText>
                        </MenuItem>}
                        {acciones.mostrarRectificar && <MenuItem
                          onClick={() => {
                            setAnchorEl(null);
                            openModalRectificar();
                          }}
                        >
                          <ListItemText>
                            Rectificar factura
                          </ListItemText>
                        </MenuItem>}

                      </Menu>
                    </>
                )}
              </Box>
              </Box>
              {/* Bloque expandible */}
              <Collapse in={expandirDetalle} timeout="auto" unmountOnExit>
                <Box
                  mt={2}
                  p={2}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    backgroundColor: "background.paper",
                    overflowX: "auto",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    Detalle de conceptos
                  </Typography>
                
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Concepto</TableCell>
                        <TableCell align="right">Cantidad</TableCell>
                        <TableCell align="right">Precio unit.</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                
                    <TableBody>
                      {facturaComplementaria?.conceptos.map((c: IConcepto, index: number) => {
                        const imp = facturaComplementaria?.impuestos?.[index];
                        const base = c.cantidad * c.precio;
                        const cuota = imp ? base * (imp.cuota / 100) : 0;
                        const total = base + cuota;
                      
                        return (
                          <React.Fragment key={index}>
                            {/* LÍNEA PRINCIPAL */}
                            <TableRow hover>
                              <TableCell>{c.nombre}</TableCell>
                              <TableCell align="right">{c.cantidad}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(c.precio, monedaFactura)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(base, monedaFactura)}
                              </TableCell>
                            </TableRow>
                        
                            {/* LÍNEA DETALLE: IMPUESTO + TOTAL */}
                            {imp && (
                              <TableRow>
                                <TableCell
                                  colSpan={4}
                                  sx={{
                                    pl: 6,
                                    py: 0.8,
                                    backgroundColor: "action.hover",
                                    borderBottom: "none",
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    {imp.tipo} ({imp.cuota}%): {formatCurrency(cuota, monedaFactura)}
                                    &nbsp;•&nbsp;
                                    <strong>Total: {formatCurrency(total, monedaFactura)}</strong>
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
                    
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose}>
                Cerrar
              </Button>
              {facturaComplementaria?.estado !== "Emitida" && <Button
                onClick={() => setModalEditarComplementaria(true)}
                disabled={facturaComplementaria?.estado === "Anulada"}
                variant="contained"
                color="primary"
                size="small"
              >
                Editar
              </Button>}
            </DialogActions>
          </>}
        </Dialog>
        
        {/* Modal pagar */}
        {modalPagar && (
        <Dialog open={modalPagar} onClose={loadingPagar ? undefined : closeModalPagar} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold" }}>
            {facturaComplementaria?.estadoPago === "Pagada" ? "Corregir cobro" : "Pagar factura"}
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            {/* Importe total */}
            <Box
              sx={{
                mb: 3,
                mt: 1,
                p: 2,
                borderRadius: 2,
                bgcolor: "grey.100",
                textAlign: "center"
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "success.main" }}>
                {formatCurrency(Number(facturaComplementaria?.importeTotal), monedaFactura)}
              </Typography>
            </Box>
            
            {/* Método de pago */}
            <FormControl fullWidth error={!!errorMetodoPago}>
              <InputLabel id="metodo-pago-label">Método de pago*</InputLabel>
              <Select
                labelId="metodo-pago-label"
                label="Método de pago*"
                value={metodoPago?.nombre || ""}
                onChange={(e) => {
                  const nombre = e.target.value;
                  const codigo = metodosPago.find((m) => m.nombre === nombre)?.codigo!;
                  setMetodoPago({ nombre, codigo, fechaPago: moment() });
                  setErrorMetodoPago(undefined);
                }}
              >
                {metodosPago
                  .filter((m) => m.activo)
                  .map((m) => (
                    <MenuItem key={m.codigo} value={m.nombre}>
                      {m.nombre}
                    </MenuItem>
                  ))}
              </Select>
                
              {errorMetodoPago && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                  {errorMetodoPago}
                </Typography>
              )}
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Fecha de pago"
                value={metodoPago?.fechaPago}
                onChange={(date) => {
                  if (metodoPago) {
                    setMetodoPago({ ...metodoPago, fechaPago: date ?? moment() });
                    setErrorFechaPago(undefined);
                  }
                }}
                renderInput={(params) => <TextField required fullWidth sx={{ mt: 2 }} error={!!errorFechaPago} helperText={errorFechaPago} {...params} />}
              />
            </LocalizationProvider>
          </DialogContent>              
          <DialogActions sx={{ p: 2 }}>
            <Button 
              variant="contained" 
              color="success"
              onClick={pagarFactura}
              disabled={loadingPagar}
              startIcon={loadingPagar && <CircularProgress size={18} />}
            >
              Pagar
            </Button>
            
            <Button 
              variant="outlined" 
              color="inherit"
              onClick={closeModalPagar}
              disabled={loadingPagar}
            >
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
        )}
        {/* Modal emitir */}
        {modalEmitir && 
        <Dialog open={modalEmitir} onClose={loadingEmitir ? undefined : closeModalEmitir} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold" }}>Emitir factura</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            {erroresPaciente.length > 0 ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                No se puede emitir la factura. Faltan datos importantes:
                <ul>
                  {erroresPaciente.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </Alert>
            ) : 
            <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {!facturaComplementaria?.receptor?.direccion && (
                <>La factura se emitirá sin dirección del paciente.<br /><br /></>
              )}
              Una vez emitida, la factura no se podrá modificar.
              Solo se podrá generar una factura rectificativa.
            </Alert>

            <NumeroOrientativoEmision info={numeroOrientativo} />


            <Typography>
              ¿Deseas emitir esta factura definitivamente?
            </Typography>
            </>
            }

          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            {erroresPaciente.length === 0 ? (
              <>
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={emitirFactura}
                  disabled={loadingEmitir}
                  startIcon={loadingEmitir && <CircularProgress size={18} />}
                >
                  Emitir
                </Button>
            
                <Button 
                  variant="outlined" 
                  color="inherit"
                  onClick={closeModalEmitir}
                  disabled={loadingEmitir}
                >
                  Cancelar
                </Button>
              </>
            ) : (
              <Button 
                variant="outlined" 
                color="inherit"
                onClick={closeModalEmitir}
              >
                Cerrar
              </Button>
            )}
          </DialogActions>
        </Dialog>}

        {/* Modal editar complementaria */}
        {modalEditarComplementaria && (
          <ComplementariaFormDialog
            open={modalEditarComplementaria}
            onClose={() => setModalEditarComplementaria(false)}
            facturasParciales={facturasParciales}
            pacientes={pacientes}
            isEditMode={true}
            isRectificativa={facturaComplementaria?.facturaOriginalId ? true : false}
            facturaComplementariaEdit={facturaComplementaria!}
            onFacturaActualizada={({ facturaComplementaria, parcialesAsociadas, parcialesDesasociadas }) => {
              setFacturaComplementaria(facturaComplementaria);
              setFacturasParcialesIds(facturaComplementaria.conceptos.map(c => c.facturaParcialId).filter((n): n is string => !!n));
              onFacturaActualizada({ facturaComplementaria, parcialesAsociadas, parcialesDesasociadas });
            }}
          />
        )}

        {/* Modal crear rectificativa */}
        {modalRectificar && (
          <ComplementariaFormDialog
            open={modalRectificar}
            onClose={() => setModalRectificar(false)}
            facturasParciales={[]}
            pacientes={pacientes}
            isRectificativa={true}
            facturaComplementariaEdit={facturaComplementaria!}
            onFacturaRectificada={({
              factura,
              tipoFacturacion,
              paciente
            }) => {
              const facturaReducida: IFactura = {
                _id: factura._id,
                numero: factura.numero,
                fechaFactura: factura.fechaFactura.toString(),
                tipo: factura.tipo,
                modalidad: tipoFacturacion,
                servicio: factura.servicio,
                pacienteId: paciente?._id || null,
                paciente: factura.receptor.nombre,
                servicioId: "",
                total: factura.importeTotal,
                estado: getEstadoFacturacion(factura.estado, factura.estadoPago),
                facturaRelacionada: undefined,
                entityId: factura.entityId ?? null,
              };
              if (onFacturaRectificada) onFacturaRectificada({ factura: facturaReducida });
              setFacturaComplementaria(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  facturaRectificada: facturaReducida.numero,
                  facturaRectificadaId: facturaReducida._id
                };
              });
            }}
          />
        )}

        <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
          <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
            {alert.msg}
          </Alert>
        </Snackbar>
      </>
    );
};

export default DetalleFacturaComplementaria;